import {
  ManageableProduct,
  ProductFilters,
  ProductType,
  useManageProducts,
  useProducts,
} from '@airship-platform/airship-proposal';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useAppointmentProposalOptionProducts } from '../../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { useAppointments } from '../../../data-access/appointments';
import ROUTE_PATHS from '../../../routes/paths';

export interface NavigateToAddProductsConfig {
  productTypes: ProductType[];
  groupingName: ManageableProduct['groupingName'];
  productPageTitle: string;
  replaceProductId?: string;
  itemGroupRootId?: string;
  categoryIds?: string[];
}

const sanitize = <T extends string>(values: string[]) =>
  [...new Set(values.map((d) => d.trim()).filter(Boolean))].sort() as T[];

export const useProductNavigationActions = () => {
  const { currentOption } = useAppointmentProposalOptions();
  const { proposalProducts } = useAppointmentProposalOptionProducts();
  const { updateProductsFilter } = useProducts();
  const { setProductDefaults, setDisplaySettings } = useManageProducts();
  const { currentAppointment } = useAppointments();
  const navigate = useNavigate();

  const isReadOnly = currentOption?.isReadOnly ?? false;

  const navigateToQueryProducts = useCallback(
    (config: NavigateToAddProductsConfig) => {
      const appointmentProposalId = currentOption?.appointmentProposalId;
      const appointmentProposalOptionId =
        currentOption?.appointmentProposalOptionId;
      const appointmentId = currentOption?.appointmentId;

      if (
        !appointmentProposalId ||
        !appointmentProposalOptionId ||
        !appointmentId
      ) {
        return;
      }

      const matches = proposalProducts
        .filter(
          (p) =>
            p.appointmentProposalOptionId === appointmentProposalOptionId &&
            p.groupingName === 'SYSTEM'
        )
        .map((p) => p.pricebookId);
      const businessUnitIds = currentAppointment?.job.businessUnitId
        ? [currentAppointment.job.businessUnitId]
        : [];

      const payload: Partial<ProductFilters> = {
        names: [],
        matches: sanitize(matches),
        pricebookTypes: sanitize(config.productTypes),
        businessUnitIds: sanitize(businessUnitIds),
        categoryIds: sanitize(config.categoryIds ?? []),
      };

      updateProductsFilter(payload, { reset: true });

      setProductDefaults({
        groupingName: config.groupingName,
      });

      setDisplaySettings({
        productPageTitle: config.productPageTitle,
      });

      navigate(
        ROUTE_PATHS.PROPOSAL_OPTIONS_PATH({
          appointmentId: appointmentId,
          appointmentProposalId: appointmentProposalId,
          appointmentProposalOptionId: appointmentProposalOptionId,
          extraPath: 'products',
          searchParams: {
            groupingName: config.groupingName,
            ...(config.replaceProductId && {
              replaceProductId: config.replaceProductId,
            }),
            ...(config.itemGroupRootId && {
              itemGroupRootId: config.itemGroupRootId,
            }),
          },
        })
      );
    },
    [
      currentAppointment?.job.businessUnitId,
      currentOption?.appointmentId,
      currentOption?.appointmentProposalId,
      currentOption?.appointmentProposalOptionId,
      navigate,
      proposalProducts,
      setDisplaySettings,
      setProductDefaults,
      updateProductsFilter,
    ]
  );

  return {
    navigateToQueryProducts,
    isReadOnly: isReadOnly,
  };
};
